import React, { useState, useEffect, useMemo } from "react";
import { Button, Table, Spinner, Badge } from "react-bootstrap";
import { useTable, usePagination, useGlobalFilter, useSortBy } from "react-table";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useDispatch } from "react-redux";
import { Fn_FillListData, Fn_DeleteData } from "../../store/Functions";
import { useNavigate } from "react-router-dom";

const PageList_CouponMaster = () => {
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const API_URL = `${API_WEB_URLS.MASTER}/0/token/CouponMaster`;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      Fn_FillListData(dispatch, setGridData, "gridData", API_URL + "/Id/0");
      setLoading(false);
    };
    fetchData();
  }, [dispatch, API_URL]);

  const data = useMemo(() => gridData || [], [gridData]);
  const columns = useMemo(
    () => [
      {
        Header: "Code",
        accessor: "Name",
        Cell: ({ value }) => (
          <span style={{ fontSize: 12, fontWeight: 700, color: "#E87B2D", letterSpacing: 0.5 }}>
            {value || "-"}
          </span>
        ),
      },
      {
        Header: "Discount",
        accessor: "Value",
        Cell: ({ value, row }) => (
          <span style={{ fontSize: 11, fontWeight: 600 }}>
            {row.original.Type === 2 ? `${value}% OFF` : `₹${value} Flat`}
          </span>
        ),
      },
      {
        Header: "Min Fare",
        accessor: "MinOrderAmount",
        Cell: ({ value }) => (
          <span style={{ fontSize: 11 }}>{value ? `₹${value}` : "-"}</span>
        ),
      },
      {
        Header: "Max Cap",
        accessor: "MaxDiscountAmount",
        Cell: ({ value, row }) => (
          <span style={{ fontSize: 11 }}>
            {row.original.Type === 2 && value ? `₹${value}` : "-"}
          </span>
        ),
      },
      {
        Header: "Per-User Limit",
        accessor: "UsageLimitPerUser",
        Cell: ({ value }) => (
          <span style={{ fontSize: 11 }}>
            {value === 0 || value == null ? "Unlimited" : `${value}x / user`}
          </span>
        ),
      },
      {
        Header: "1st Ride Only",
        accessor: "IsFirstOrderOnly",
        Cell: ({ value }) => (
          <Badge bg={value ? "info" : "light"} text={value ? "white" : "dark"} style={{ fontSize: 10 }}>
            {value ? "1st Ride Only" : "All Rides"}
          </Badge>
        ),
      },
      {
        Header: "Available Stock",
        accessor: "CouponCount",
        Cell: ({ value }) => (
          <span style={{ fontSize: 11, fontWeight: 500, color: value <= 10 ? "#dc3545" : "#28a745" }}>
            {value ?? "-"}
          </span>
        ),
      },
      {
        Header: "Total Used",
        accessor: "TotalUsed",
        Cell: ({ value }) => (
          <span style={{ fontSize: 11, fontWeight: 600 }}>{value ?? 0}</span>
        ),
      },
      {
        Header: "Status",
        accessor: "IsActive",
        Cell: ({ value }) => (
          <Badge bg={value !== false ? "success" : "secondary"} style={{ fontSize: 10 }}>
            {value !== false ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        Header: "Expiry",
        accessor: "ValidTillDate",
        Cell: ({ value }) => {
          if (!value) return <span style={{ fontSize: 11 }}>-</span>;
          const isExpired = new Date(value) < new Date();
          return (
            <span style={{ fontSize: 11, color: isExpired ? "#dc3545" : "inherit" }}>
              {new Date(value).toLocaleDateString()}
              {isExpired && " (Expired)"}
            </span>
          );
        },
      },
      {
        id: "actions",
        Header: "Actions",
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: 4 }}>
            <Button
              size="sm"
              variant="outline-primary"
              className="py-0 px-2"
              style={{ fontSize: 11 }}
              onClick={() => navigate("/AddCouponMaster", { state: { Id: row.original.Id } })}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              className="py-0 px-2"
              style={{ fontSize: 11 }}
              onClick={() => {
                if (window.confirm("Are you sure you want to delete this coupon?"))
                  Fn_DeleteData(dispatch, row.original.Id, API_URL, API_URL + "/Id/0").then(
                    (r) => r?.success && Fn_FillListData(dispatch, setGridData, "gridData", API_URL + "/Id/0")
                  );
              }}
            >
              Del
            </Button>
          </div>
        ),
      },
    ],
    [dispatch, navigate, API_URL]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = useTable(
    { columns, data, initialState: { pageIndex: 0, pageSize: 15, sortBy: [{ id: "Id", desc: true }] } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  if (loading)
    return (
      <div style={{ padding: "6px 8px", display: "flex", justifyContent: "center", alignItems: "center", minHeight: 120 }}>
        <Spinner animation="border" size="sm" />
      </div>
    );

  return (
    <div style={{ padding: "6px 8px", maxWidth: 1200, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 6,
          paddingBottom: 4,
          borderBottom: "1px solid #dee2e6",
        }}
      >
        <div>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>Coupon Management</span>
          <span className="text-muted ms-2" style={{ fontSize: 12 }}>({data.length} total coupons)</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="text"
            placeholder="Search coupons..."
            value={globalFilter || ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="form-control form-control-sm"
            style={{ width: 160, height: 28, fontSize: 12 }}
          />
          <Button
            size="sm"
            variant="primary"
            className="py-1 px-3"
            style={{ fontSize: 12, height: 28 }}
            onClick={() => navigate("/AddCouponMaster", { state: { Id: 0 } })}
          >
            + Add Coupon
          </Button>
        </div>
      </div>

      <div style={{ overflowX: "auto", marginBottom: 6 }}>
        <Table {...getTableProps()} size="sm" bordered hover style={{ fontSize: 12, minWidth: 800 }}>
          <thead>
            {headerGroups.map((headerGroup, i) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                {headerGroup.headers.map((col, j) => (
                  <th
                    {...col.getHeaderProps(col.getSortByToggleProps())}
                    key={j}
                    style={{
                      padding: "5px 8px",
                      fontSize: 11,
                      fontWeight: 600,
                      backgroundColor: "#E87B2D",
                      color: "#fff",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col.render("Header")}
                    {col.isSorted && <span style={{ marginLeft: 3 }}>{col.isSortedDesc ? "▼" : "▲"}</span>}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-3 text-muted" style={{ fontSize: 12 }}>
                  No coupons found. Click "+ Add Coupon" to create one.
                </td>
              </tr>
            ) : (
              page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={i}>
                    {row.cells.map((cell, j) => (
                      <td {...cell.getCellProps()} key={j} style={{ padding: "4px 8px", verticalAlign: "middle" }}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11, flexWrap: "wrap", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>
            Page {pageIndex + 1} of {pageOptions.length || 1}
          </span>
          <select
            className="form-select form-select-sm"
            style={{ width: 60, height: 26, fontSize: 11 }}
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[10, 15, 20, 30, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-2" style={{ fontSize: 11 }} onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
            |«
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-2" style={{ fontSize: 11 }} onClick={() => previousPage()} disabled={!canPreviousPage}>
            ‹
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-2" style={{ fontSize: 11 }} onClick={() => nextPage()} disabled={!canNextPage}>
            ›
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-2" style={{ fontSize: 11 }} onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
            »|
          </button>
        </div>
      </div>
    </div>
  );
};

export default PageList_CouponMaster;
